/**
 * FP3-033 — Print/Display shared layout primitives.
 *
 * `PrintShell` wraps a print route. It owns the print-only stylesheet
 * import and the slim screen-only toolbar (Print + Back). On paper the
 * toolbar is hidden via `@media print` so only the actual content
 * lands on the page.
 *
 * `DisplayShell` wraps a display route. It shows a slim top bar with
 * a "Print" shortcut + an "Exit display" action that history-backs
 * when possible and falls back to closing the tab.
 *
 * The shells are intentionally tiny and self-contained — they DO NOT
 * pull in AppLayout / sidebar / nav. They use the printable surface
 * styles in /app/frontend/src/styles/print.css.
 */
import { Printer, ArrowLeft, X } from "@phosphor-icons/react";
import "@/styles/print.css";

function tryHistoryBackElseClose() {
  // Attempt history back first (works when opened in same tab).
  // If we landed here directly (new tab), window.history.length is
  // typically 1 or 2 and there's nothing to go back to → close instead.
  if (typeof window === "undefined") return;
  const len = window.history.length;
  if (len > 1) {
    // FP3-033 — prefer history back so deep links + same-tab opens
    // return cleanly. New-tab opens (target="_blank") usually have
    // length === 1, in which case we close.
    window.history.back();
    // Belt: if history-back is a no-op (e.g. same SPA stuck), close
    // after a short wait. Most browsers swallow `window.close()` on
    // user-opened tabs but not on tabs opened via window.open() /
    // target="_blank" — which is our actual flow.
    setTimeout(() => {
      try { window.close(); } catch (_) { /* noop */ }
    }, 250);
  } else {
    try { window.close(); } catch (_) { /* noop */ }
  }
}

export function PrintShell({ title, subtitle, footerNote, children, landscape = false, testId }) {
  return (
    <div
      data-testid={testId || "fp-print-shell"}
      data-fp-mode="print"
      className={`fp-print-page${landscape ? " fp-landscape" : ""}`}
    >
      <div className="fp-print-toolbar" data-fp-no-print="true">
        <button
          type="button"
          data-testid="print-toolbar-back"
          className="fp-print-action"
          onClick={tryHistoryBackElseClose}
        >
          <ArrowLeft size={12} weight="bold" style={{ display: "inline", marginRight: 6, verticalAlign: "-1px" }} />
          Back
        </button>
        <div className="fp-print-toolbar-spacer" />
        <button
          type="button"
          data-testid="print-toolbar-print"
          className="fp-print-action primary"
          onClick={() => window.print()}
        >
          <Printer size={12} weight="bold" style={{ display: "inline", marginRight: 6, verticalAlign: "-1px" }} />
          Print
        </button>
      </div>
      {title && <h1 className="fp-print-title">{title}</h1>}
      {subtitle && <p className="fp-print-subtitle">{subtitle}</p>}
      {children}
      <div className="fp-print-footer">
        <span>Frame Practice</span>
        <span>{footerNote || `Generated ${new Date().toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}`}</span>
      </div>
    </div>
  );
}

export function DisplayShell({ title, subtitle, children, testId, leftSlot, rightSlot }) {
  return (
    <div
      data-testid={testId || "fp-display-shell"}
      data-fp-mode="display"
      className="fp-display-page"
    >
      <div className="fp-display-toolbar" data-fp-no-print="true">
        <button
          type="button"
          data-testid="display-toolbar-exit"
          className="fp-display-action"
          onClick={tryHistoryBackElseClose}
        >
          <X size={13} weight="bold" style={{ display: "inline", marginRight: 6, verticalAlign: "-1px" }} />
          Exit display
        </button>
        {leftSlot}
        <div className="fp-display-toolbar-spacer" />
        {rightSlot}
        <button
          type="button"
          data-testid="display-toolbar-print"
          className="fp-display-action primary"
          onClick={() => window.print()}
        >
          <Printer size={13} weight="bold" style={{ display: "inline", marginRight: 6, verticalAlign: "-1px" }} />
          Print
        </button>
      </div>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        {title && <h1 className="fp-display-h1">{title}</h1>}
        {subtitle && <p className="fp-display-sub">{subtitle}</p>}
      </div>
      {children}
    </div>
  );
}

export function PrintAccessGate({ phase, error, children }) {
  // Shared empty/error/loading panel. `phase` is one of:
  //   "loading" | "ready" | "forbidden" | "not_found" | "error"
  if (phase === "loading") {
    return (
      <div className="fp-print-empty" data-testid="fp-print-loading">
        Loading…
      </div>
    );
  }
  if (phase === "forbidden") {
    return (
      <div className="fp-print-empty" data-testid="fp-print-forbidden">
        You don’t have access to view this content.
      </div>
    );
  }
  if (phase === "not_found") {
    return (
      <div className="fp-print-empty" data-testid="fp-print-not-found">
        We couldn’t find that record. It may have been removed.
      </div>
    );
  }
  if (phase === "error") {
    return (
      <div className="fp-print-empty" data-testid="fp-print-error">
        {error || "Something went wrong loading this view."}
      </div>
    );
  }
  return children;
}

export function classifyError(err) {
  const status = err?.response?.status;
  if (status === 401 || status === 403) return "forbidden";
  if (status === 404) return "not_found";
  return "error";
}
